import { createContext, useMemo, useState } from 'react';

export type Footnote = { key: string; value: string };

type FootnoteContextType = {
	/**
	 * Adds a footnote to the context. Keys must be unique.
	 */
	addFootnote: (key: string, value: string) => void;
	/**
	 * Removed a footnote.
	 *
	 * @example
	 * removeFootnote('carbon-dioxide-emissions')
	 */
	removeFootnote: (key: string) => void;
	/**
	 * Returns the reference (i.e., number) for the given footnote key.
	 */
	getReference: (key: string) => string;
	/**
	 * Returns all footnotes that were added to the context.
	 */
	getFootnotes: () => Footnote[];
};

export const FootnoteContext = createContext(null as unknown as FootnoteContextType);

export function FootnoteProvider({ children }: { children?: React.ReactNode }) {
	const [footnotes, setFootnotes] = useState([] as Footnote[]);

	const ctxValue: FootnoteContextType = useMemo(() => {
		return {
			getReference: (key) => {
				const index = footnotes.findIndex((f) => f.key === key);
				return index >= 0 ? String.fromCharCode(index + 'a'.charCodeAt(0)) : '';
			},
			addFootnote: (key, value) => {
				if (value) {
					setFootnotes((ft) => {
						const newFootnotes = structuredClone(ft);
						if (newFootnotes.find((d) => d.key === key)) {
							// If the footnote already exists, update it
							const index = newFootnotes.findIndex((f) => f.key === key);
							newFootnotes[index].value = value;
						} else {
							// If the footnote does not exist, add it
							newFootnotes.push({ key, value });
						}
						return newFootnotes;
					});
				}
			},
			removeFootnote: (key) => {
				const newFootnotes = footnotes.filter((f) => f.key !== key);
				setFootnotes(newFootnotes);
			},
			getFootnotes() {
				return footnotes;
			},
		};
	}, [JSON.stringify(footnotes)]);

	return <FootnoteContext.Provider value={ctxValue}>{children}</FootnoteContext.Provider>;
}
